// Pure decision helpers for the chip-placement submission lifecycle.
// Extracted from ChipPlacementPanel so the timing/freshness logic that
// guards against lost, stale and wrongly-recorded bets is unit-testable
// without rendering the 3k-line panel or standing up sockets/stores.

/** The auto-submit timer delay: chips submit ~1.5s into the closing phase. */
export const SUBMIT_TIMER_MS = 1500

/**
 * Wall-clock overrun beyond which the submit timer is treated as having been
 * paused by a backgrounded/frozen tab (so the round it was armed for has
 * almost certainly closed → hold for the next round).
 *
 * This is measured PURELY on the local clock — the gap between arming the
 * 1.5s timer and it actually firing. A frozen/discarded mobile tab pauses
 * `setTimeout`, so the timer fires many seconds late (elapsed = the freeze
 * duration). A SLOW NETWORK does NOT inflate this: the timer runs on the
 * local clock, unaffected by request latency. That distinction is the whole
 * point — the previous server-timestamp comparison conflated the two and
 * held live bets forever on bad connections. Generous margin over the
 * 1.5s + normal main-thread jank; real freezes are whole rounds (15-60s+).
 * Does NOT touch the server-side anti-front-running tolerances.
 */
export const FREEZE_HOLD_THRESHOLD_MS = 4500

/**
 * True when the armed submit timer took far longer (wall-clock) than it was
 * set for — the signature of a paused/frozen tab, NOT a slow network.
 * `timerArmedAt === undefined` (e.g. the race_started fallback, which has no
 * armed timer) ⇒ never hold (fail open — a missed hold only risks a
 * refunded reject, whereas a wrong hold blocks a live bet).
 */
export function isFrozenSubmission(params: {
  timerArmedAt: number | undefined
  now: number
  /**
   * The delay the timer was armed with when it is not the 1.5 s submit timer —
   * Birdie's close backstop is armed a whole betting window ahead
   * (`submitBackstop.ts`). The overrun is measured past THAT delay, so the
   * same 4.5 s threshold reads the same way: a timer that fired far later than
   * it was set for. Defaults to `SUBMIT_TIMER_MS`, which leaves every existing
   * caller byte-identical.
   */
  timerDelayMs?: number
}): boolean {
  if (params.timerArmedAt === undefined) return false
  const extraDelay = Math.max(0, (params.timerDelayMs ?? SUBMIT_TIMER_MS) - SUBMIT_TIMER_MS)
  return params.now - params.timerArmedAt - extraDelay > FREEZE_HOLD_THRESHOLD_MS
}

/**
 * Classify the post-submit lifecycle transition from a submit result.
 *
 * - `betsActuallySent` gates the round-transition board reset: only a
 *   submission that actually put bets on the wire should mark the round as
 *   "done" (→ clear the board). An aborted submit that pushed nothing must
 *   NOT set it, or the chips silently vanish at round end though nothing
 *   was ever bet.
 * - `unlockForRetry` is true when a submit was ABORTED by a transient
 *   failure (socket not joined / no channel / no race) while chips were on
 *   the board and nothing went out. The caller then unlocks the submission
 *   flags so the chips carry forward and re-submit next round rather than
 *   looking "live" all race and then disappearing.
 */
export function classifySubmitOutcome(params: {
  sent: number
  aborted: boolean
  attemptedChips: number
}): { betsActuallySent: boolean; unlockForRetry: boolean } {
  return {
    betsActuallySent: params.sent > 0,
    unlockForRetry: params.aborted && params.attemptedChips > 0 && params.sent === 0
  }
}

/**
 * B4: detect a missed `race_ready`. The per-round submission reset is
 * driven by `race_ready`; if that event is dropped, the previous round's
 * completed submission flags survive into the new round and the single-shot
 * guard silently blocks the new round from ever submitting.
 *
 * We recover at `race_starting` when we still hold a submission that
 * belongs to a DIFFERENT (previous) round. The `submittedRoundId !==
 * currentRoundId` guard is essential: when they match we are mid-round and
 * the settle-reconcile still depends on these flags — resetting then would
 * disarm it.
 */
export function shouldRecoverMissedRoundReset(params: {
  submissionStarted: boolean
  submittedRoundId: string | undefined
  currentRoundId: string | undefined
}): boolean {
  return (
    params.submissionStarted &&
    params.submittedRoundId !== undefined &&
    params.currentRoundId !== undefined &&
    params.submittedRoundId !== params.currentRoundId
  )
}

/**
 * What a hard round's lock sends from the chip panel (hard close rev 4, DESIGN §6).
 *
 * The lock sends at once, in the same tick as the B4 recovery that runs just
 * before it, so it cannot read the board React will draw after that recovery,
 * as the 1.5 s timer could. It is told what happened instead. Nothing goes when
 * - the recovery has just cleared last round's completed board
 *   (`recoveryClearedBoard`): those chips went out as last round's bets, and the
 *   state update that drops them has not been drawn yet; or
 * - the board was rebuilt from THIS round's own bets (`restoredRoundId`: a
 *   refresh, a remount or a second tab between the lock and `race_starting`):
 *   its chips are bets already, and sending them would place each one twice.
 * Otherwise the board as it stands goes.
 */
export function hardLockBoard<T>(params: {
  chips: Record<string, T>
  currentRoundId: string | undefined
  restoredRoundId: string | undefined
  recoveryClearedBoard: boolean
}): Record<string, T> {
  if (params.recoveryClearedBoard) return {}
  if (params.restoredRoundId !== undefined && params.restoredRoundId === params.currentRoundId) {
    return {}
  }
  return params.chips
}
